import { api } from "_shared/_stores/api";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import IndividualSchema, { IndividualJSON } from "marketplace/individuals/_schema/individual-schema";
import UpdateIndividualProfileRequest from "marketplace/individuals/profiles/_api/requests-contracts/update-individual-profile-request";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import z from "zod";

export const individualsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getIndividualById: build.query<IndividualJSON, { individualId: string }>({
      query: ({ individualId }) => `marketplace/individuals/${individualId}`,
      rawResponseSchema: IndividualSchema.IndividualJSONZ,
      providesTags: (result) =>
        result ? [{ type: "Individual" as const, id: result.id }] : [{ type: "Individual" as const, id: "LIST" }],
    }),
    getIndividualsByUserIds: build.query<IndividualJSON[], { userIds: Set<string> }>({
      async queryFn({ userIds }, { getState }, _extraOptions, fetchWithBQ) {
        const state = getState() as { individuals: { byUserId: { entries: Record<string, IndividualJSON> } } };
        const byUserId = state.individuals.byUserId.entries;

        const cachedIndividuals: IndividualJSON[] = [];
        const missingUserIds: string[] = [];
        for (const userId of userIds) {
          const cached = byUserId[userId];
          if (cached) cachedIndividuals.push(cached);
          else missingUserIds.push(userId);
        }

        if (!missingUserIds.length) return { data: cachedIndividuals };

        const result = await fetchWithBQ({
          url: "marketplace/individuals-by-userids",
          method: "POST",
          body: missingUserIds,
        });
        if (result.error) return { error: result.error };

        const fetched = z.array(IndividualSchema.IndividualJSONZ).parse(result.data);
        return { data: [...cachedIndividuals, ...fetched] };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const normalized = [...new Set(queryArgs.userIds)].sort((a, b) => a.localeCompare(b));
        return `${endpointName}(${normalized.join(",")})`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Individual" as const, id: id })),
              { type: "Individual" as const, id: "LIST" },
            ]
          : [{ type: "Individual" as const, id: "LIST" }],
    }),
    getIndividualsByEntityId: build.query<IndividualJSON[], { legalEntityId: string }>({
      async queryFn({ legalEntityId }, { getState }, _extraOptions, fetchWithBQ) {
        const membersResult = await fetchWithBQ(`/legal-entities/${legalEntityId}/members/`);
        if (membersResult.error) return { error: membersResult.error };
        const members = z.array(EntityMemberSchema.EntityMemberJSONZ).parse(membersResult.data);

        const state = getState() as { individuals: { byUserId: { entries: Record<string, IndividualJSON> } } };
        const byUserId = state.individuals.byUserId.entries;

        const cachedIndividuals: IndividualJSON[] = [];
        const missingUserIds: string[] = [];
        for (const member of members) {
          const cached = byUserId[member.userId];
          if (cached) cachedIndividuals.push(cached);
          else missingUserIds.push(member.userId);
        }

        if (!missingUserIds.length) return { data: cachedIndividuals };

        const result = await fetchWithBQ({
          url: "marketplace/individuals-by-userids",
          method: "POST",
          body: missingUserIds,
        });
        if (result.error) return { error: result.error };

        const fetched = z.array(IndividualSchema.IndividualJSONZ).parse(result.data);
        return { data: [...cachedIndividuals, ...fetched] };
      },
      providesTags: (result, _err, { legalEntityId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Individual" as const, id })),
              { type: "Individual" as const, id: `entity-${legalEntityId}` },
            ]
          : [{ type: "Individual" as const, id: "LIST" }],
    }),
    getIndividualVendors: build.query<IndividualJSON[], void>({
      query: () => "marketplace/individual-vendors",
      rawResponseSchema: z.array(IndividualSchema.IndividualJSONZ),
      providesTags: [{ type: "Individual" as const, id: "LIST" }],
    }),
    getIndividualsByIds: build.query<IndividualJSON[], { ids: string[] }>({
      query: ({ ids }) => ({
        url: "marketplace/individuals-by-ids",
        method: "POST",
        body: ids,
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: z.array(IndividualSchema.IndividualJSONZ),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Individual" as const, id: id })),
              { type: "Individual" as const, id: "LIST" },
            ]
          : [{ type: "Individual" as const, id: "LIST" }],
    }),
    getIndividualAvatar: build.query<string, { individualId: string }>({
      query: ({ individualId }) => `marketplace/individuals/${individualId}/avatar`,
      providesTags: (_result, _error, { individualId }) => [{ type: "Individual" as const, id: individualId }],
    }),
    downloadIndividualResume: build.query<{ blob: Blob; filename?: string }, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individuals/${individualId}/resume`,
        responseHandler: async (response: Response) => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition");
          const matches = contentDisposition
            ? new RegExp(/(?<=filename(?:=|\*=(?:[\w-]+'')))["|']?(?<filename>[^"';\n]+)["|']?/).exec(contentDisposition)
            : null;
          return { blob, filename: matches?.groups?.filename };
        },
      }),
    }),
    // #endregion

    // #region Mutations
    updateIndividual: build.mutation<
      IndividualJSON,
      { individualId: string; isVisible: boolean; originalProfile?: IndividualProfile; updatedProfile?: IndividualProfile }
    >({
      query: ({ individualId, isVisible, originalProfile, updatedProfile }) => ({
        url: `marketplace/individuals/${individualId}`,
        method: "PATCH",
        body: UpdateIndividualProfileRequest.buildPayload(isVisible, originalProfile, updatedProfile),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: IndividualSchema.IndividualJSONZ,
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        const { data: individualJSON } = await queryFulfilled;
        if (individualJSON.id) {
          dispatch(
            individualsApi.util.upsertQueryData(
              "getIndividualById",
              { individualId: individualJSON.id },
              individualJSON,
            ),
          );
        }
      },
      invalidatesTags: () => [{ type: "Individual" as const, id: "LIST" }],
    }),
    createIndividualAvatar: build.mutation<void, { individualId: string; file: File }>({
      query: ({ individualId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `marketplace/individuals/${individualId}/avatar`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: () => [{ type: "Individual" as const, id: "LIST" }],
    }),
    createIndividualResume: build.mutation<void, { individualId: string; file: File }>({
      query: ({ individualId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `marketplace/individuals/${individualId}/resume`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: () => [{ type: "Individual" as const, id: "LIST" }],
    }),
    deleteIndividualAvatar: build.mutation<void, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individuals/${individualId}/avatar`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Individual" as const, id: "LIST" }],
    }),
    deleteIndividualResume: build.mutation<void, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individuals/${individualId}/resume`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Individual" as const, id: "LIST" }],
    }),
    contactIndividual: build.mutation<ForumJSON, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individuals/${individualId}/contact`,
        method: "POST",
        body: {},
      }),
    }),
    // #endregion
  }),
});

export const {
  useGetIndividualByIdQuery,
  useGetIndividualsByUserIdsQuery,
  useGetIndividualsByEntityIdQuery,
  useGetIndividualVendorsQuery,
  useGetIndividualsByIdsQuery,
  useGetIndividualAvatarQuery,
  useDownloadIndividualResumeQuery,
  useUpdateIndividualMutation,
  useCreateIndividualAvatarMutation,
  useCreateIndividualResumeMutation,
  useDeleteIndividualAvatarMutation,
  useDeleteIndividualResumeMutation,
  useContactIndividualMutation,
} = individualsApi;
